import Typography from "@material-ui/core/Typography";
import { showSnackbar } from "app/main/utils/snackbarUtil";
import { incrementDataRevision } from "app/store/tools/revisionSlice";
import { openFormDialog } from "app/store/tools/formDialogSlice";
import { toggleEmailProvider, testEmailProvider } from "app/services/carrierServices";

const flagBadge = (active, label) => (
	<Typography
		key={label}
		className={`text-12 px-6 py-2 mr-4 rounded inline-block ${
			active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-500"
		}`}
	>
		{label}
	</Typography>
);

const carrierEmailProvidersList = {
	urlGET: "api/profile/carriers/:carrierId/email-providers",
	idKey: "_id",
	listenEvent: "profileRevision",
	segmentKey: "PROFILE_EMAIL_PROVIDERS",
	content: {
		grid: {
			tilesPerRow: 2,
			itemsPerRow: 1,
			mobile: {
				tilesPerRow: 1,
				itemsPerRow: 2,
				layout: [["provider", "username"]],
			},
		},
		table: {
			variant: "skinny",
			theme: "dark",
		},
		items: [
			{
				key: "_id",
				label: "Provider",
				table: { align: "left" },
				builder: (item) => {
					if (item.provider === "office365") {
						return <Typography className="text-13 max-w-180 truncate">Office 365</Typography>;
					} else if (item.provider === "gmail") {
						return <Typography className="text-13 max-w-180 truncate">Gmail</Typography>;
					} else {
						return <Typography className="text-13 max-w-180 truncate">{item.server_name || "SMTP"}</Typography>;
					}
				},
			},
			{
				key: "username",
				label: "Email",
				table: { align: "left" },
				builder: (item) => {
					return <Typography className="text-13 max-w-180 truncate">{item.authentication?.username || "-"}</Typography>;
				},
			},
			{
				key: "usage",
				label: "Used For",
				table: { align: "left" },
				builder: (item) => (
					<div className="flex flex-wrap">
						{flagBadge(!!item.use_for_broker_updates, "Broker Updates")}
						{flagBadge(!!item.use_for_booking, "Booking")}
						{flagBadge(!!item.use_for_invoicing, "Invoicing")}
					</div>
				),
			},
			{
				key: "enabled",
				type: "text",
				label: "Status",
				table: { width: "12%" },
				builder: (item) => {
					if (item.enabled) {
						return <Typography className="text-13 max-w-180 truncate">Enabled</Typography>;
					} else {
						return <Typography className="text-13 max-w-180 truncate text-grey">Disabled</Typography>;
					}
				},
			},
			{
				key: "menu",
				type: "menu",
				label: "",
				items: [
					{
						key: "view",
						label: "View",
						dialogBuilder: (item, dataIds) => ({
							formId:
								item?.authentication?.type === "OAUTH"
									? "CARRIER_EMAIL_PROVIDER_OAUTH_FORM"
									: "CARRIER_EMAIL_PROVIDER_FORM",
							mode: "VIEW",
							dataIds: { providerId: item._id, carrierId: dataIds.carrierId },
						}),
					},
					{
						key: "edit",
						label: "Edit",
						dialogBuilder: (item, dataIds) => ({
							formId:
								item?.authentication?.type === "OAUTH"
									? "CARRIER_EMAIL_PROVIDER_OAUTH_FORM"
									: "CARRIER_EMAIL_PROVIDER_FORM",
							mode: "EDIT",
							dataIds: { providerId: item._id, carrierId: dataIds.carrierId },
						}),
					},
					{
						key: "reauthenticate",
						label: "Re-authenticate",
						disabled: (item) => item?.authentication?.type !== "OAUTH",
						dispatchAction: (item, dataIds, dispatch) => {
							dispatch(
								openFormDialog({
									viewId: "CARRIER_EMAIL_AUTH_TYPE",
									dataIds: { carrierId: dataIds.carrierId, target: "provider", providerId: item._id },
								})
							);
						},
					},
					{
						key: "test",
						label: "Test",
						type: "button",
						dispatchAction: async (item, dataIds, dispatch, history, snackbar) => {
							try {
								await testEmailProvider(dataIds.carrierId, item._id);
								showSnackbar(snackbar, "Test email sent successfully", "success");
							} catch (error) {
								showSnackbar(snackbar, error?.errors?.[0]?.message || "Failed to send test email", "error");
							}
						},
					},
					{
						key: "toggle",
						label: "Enable / Disable",
						type: "button",
						dispatchAction: async (item, dataIds, dispatch, history, snackbar) => {
							try {
								await toggleEmailProvider(dataIds.carrierId, item._id);
								dispatch(incrementDataRevision({ event: "profileRevision" }));
							} catch (error) {
								showSnackbar(snackbar, error?.errors?.[0]?.message || "Failed to toggle provider", "error");
							}
						},
					},
					{
						key: "delete",
						label: "Delete",
						dialogBuilder: (item, dataIds) => ({
							formId:
								item?.authentication?.type === "OAUTH"
									? "CARRIER_EMAIL_PROVIDER_OAUTH_FORM"
									: "CARRIER_EMAIL_PROVIDER_FORM",
							mode: "DELETE",
							dataIds: { providerId: item._id, carrierId: dataIds.carrierId },
						}),
					},
				],
			},
		],
	},
};

export default carrierEmailProvidersList;
